import { always, applySpec, complement, cond, equals, flatten, ifElse, isNil, map, pipe, prop, propEq } from 'ramda';
import { Element as ScrollerElement } from 'react-scroll';

import { BLOCK_TYPES, CATEGORIES } from './constants';
import { CardCarousel } from '../components/cardCarousel';
import { CardGrid } from '../components/cardGrid';
import { CardNumber } from '../components/cardNumber';
import { Chart } from '../components/chart';
import { ChartCarousel } from '../components/chartCarousel';
import { Content } from '../components/content';
import { Hero } from '../components/hero';
import { Image } from '../components/imageComponent';
import { Video } from '../components/video';
import { Table } from '../components/table';
import { Timeline } from '../components/timeline';
import { VideoCardCarousel } from '../components/videoCardCarousel';
import { VideoCard } from '../components/videoCard';
import { BigNumber } from '../components/bigNumber/bigNumber.component';
import { RowCard } from '../components/rowCard';
import { ColumnCard } from '../components/columnCard/columnCard.component';
import { UpdatedCardGrid } from '../components/updatedCardGrid';
import React from 'react';
import { PAGES_CATEGORIES } from '../templates/reportPage/reportPage.constants';
import styled, { ThemeProvider } from 'styled-components';
import { COLORS_SETS, RED_COLORS_SET, GREEN_COLORS_SET } from './colors';

import { useIntl } from "gatsby-plugin-intl";

export const Page = styled(ScrollerElement)`
  display: flex;
  flex-direction: column;

  @media print {
    display: block;
    page-break-inside: avoid;
  }
`;

export const renderWhen = (pred, fn) => ifElse(pred, fn, always(null));

export const renderWhenNotNil = (fn) => renderWhen(complement(isNil), fn);

export const renderWhenTrue = (fn) => renderWhen(equals(true), fn);

export const renderWhenTrueOtherwise = (fn, otherwise) => ifElse(equals(true), fn, otherwise);

const {
  CARD_CAROUSEL,
  CARD_GRID,
  CARD_NUMBER,
  CHART,
  CHART_CAROUSEL,
  CONTENT,
  HERO,
  IMAGE,
  TABLE,
  VIDEO,
  REFERENCES,
  TIMELINE,
  VIDEO_CARD_CAROUSEL,
  VIDEO_CARD,
  BIG_NUMBER,
  ROW_CARD,
  COLUMN_CARD,
  UPDATED_CARD_GRID
  // ABOUT_MODAL,
} = BLOCK_TYPES;

const cardCarouselRenderer = ({ elements }, key) => <CardCarousel elements={elements} key={key} />;

const cardGridRenderer = ({ elements }, key) => <CardGrid elements={elements} key={key} />;

const cardNumberRenderer = ({ elements }, key) => <CardNumber elements={elements} key={key} />;

const chartRenderer = ({ elements }, key) => <Chart elements={elements} key={key} />;

const chartCarouselRenderer = ({ elements }, key) => <ChartCarousel elements={elements} key={key} />;

const contentRenderer = ({ elements }, key) => <Content elements={elements} key={key} isExpandable={false} />;

const heroRenderer = ({ elements }, key) => <Hero elements={elements} key={key} />;

const imageRenderer = ({ elements }, key) => <Image elements={elements} key={key} />;

const videoRenderer = ({ elements }, key) => <Video elements={elements} key={key} />;

const tableRenderer = ({ elements }, key) => <Table elements={elements} key={key} />;

const referencesRenderer = ({ elements }, key) => <Content elements={elements} key={key} />;

const timelineRenderer = ({elements}, key) => <Timeline elements={elements} key={key} />;

const videoCardCarouselRenderer = ({elements}, key) => <VideoCardCarousel elements={elements} key={key} />;

const videoCardRenderer = ({elements}, key) => <VideoCard elements={elements} key={key} />;

const bigNumberRenderer = ({elements}, key) => <BigNumber elements={elements} key={key} />;

const rowCardRenderer = ({elements}, key) => <RowCard elements={elements} key={key} />;

const columnCardRenderer = ({elements}, key) => <ColumnCard elements={elements} key={key} />;

const updatedCardGridRenderer = ({elements}, key) => <UpdatedCardGrid elements={elements} key={key} />;

const eqRenderer = (type) => propEq('name', type);

export const renderBlock = cond([
  [eqRenderer(CARD_CAROUSEL), cardCarouselRenderer],
  [eqRenderer(CARD_GRID), cardGridRenderer],
  [eqRenderer(CARD_NUMBER), cardNumberRenderer],
  [eqRenderer(CHART), chartRenderer],
  [eqRenderer(CHART_CAROUSEL), chartCarouselRenderer],
  [eqRenderer(CONTENT), contentRenderer],
  [eqRenderer(HERO), heroRenderer],
  [eqRenderer(IMAGE), imageRenderer],
  [eqRenderer(TABLE), tableRenderer],
  [eqRenderer(VIDEO), videoRenderer],
  [eqRenderer(REFERENCES), referencesRenderer],
  [eqRenderer(TIMELINE), timelineRenderer],
  [eqRenderer(VIDEO_CARD_CAROUSEL), videoCardCarouselRenderer],
  [eqRenderer(VIDEO_CARD), videoCardRenderer],
  [eqRenderer(BIG_NUMBER), bigNumberRenderer],
  [eqRenderer(ROW_CARD), rowCardRenderer],
  [eqRenderer(COLUMN_CARD), columnCardRenderer],
  [eqRenderer(UPDATED_CARD_GRID), updatedCardGridRenderer]
]);

export const renderPage = (data) => ({ blocks, subSectionId, section }, index) => {
  if (subSectionId === CATEGORIES.MONGOLIA) {
    const pageBlocks = pipe(
      map(
        applySpec({
          elements: prop('elements'),
          name: prop('name'),
          data: always(data),
        })
      ),
      flatten
    )(blocks);

    return (
      <ThemeProvider key={index} theme={RED_COLORS_SET}>
        <Page name={subSectionId}>{pageBlocks.map(renderBlock)}</Page>
      </ThemeProvider>
    );
  } if (subSectionId === CATEGORIES.COLOMBIA) {
    const pageBlocks = pipe(
      map(
        applySpec({
          elements: prop('elements'),
          name: prop('name'),
          data: always(data),
        })
      ),
      flatten
    )(blocks);

    return (
      <ThemeProvider key={index} theme={GREEN_COLORS_SET}>
        <Page name={subSectionId}>{pageBlocks.map(renderBlock)}</Page>
      </ThemeProvider>
    );
  } else {
    const category = PAGES_CATEGORIES[section.slug] || PAGES_CATEGORIES.BACKGROUND;
    const pageBlocks = pipe(
      map(
        applySpec({
          elements: prop('elements'),
          name: prop('name'),
          data: always(data),
        })
      ),
      flatten
    )(blocks);

    return (
      <ThemeProvider key={index} theme={COLORS_SETS[category] || COLORS_SETS[CATEGORIES.BACKGROUND]}>
        <Page name={subSectionId}>{pageBlocks.map(renderBlock)}</Page>
      </ThemeProvider>
    );
  }
};
